// crewbit-admin/src/pages/Vendors/VendorDetailPanel.tsx
//
// Wave 7 Step 4 — read-only vendor detail. Slide-out panel mirror of
// Clients/ClientDetailPanel. Header info + stats row + Edit affordance that
// hands off to VendorFormPanel (edit mode).
//
// The list view holds VendorWithTotals (running totals); this panel takes the
// same shape — no second fetch needed for the v1 detail view. Future
// transaction-timeline mirror of the mobile detail screen lands as a
// follow-up if/when the founder asks for it.

import type { CSSProperties } from 'react';
import { Edit2 } from 'lucide-react';
import { palette, spacing, radii, admin } from '../../styles/tokens';
import { SlidePanel } from '../../components/common/SlidePanel';
import type { VendorWithTotals } from './hooks';

interface Props {
  vendor: VendorWithTotals | null;
  onClose: () => void;
  onEdit: () => void;
}

function fmt$(n: number): string {
  return `$${n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso + (iso.length === 10 ? 'T00:00:00' : ''));
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function VendorDetailPanel({ vendor, onClose, onEdit }: Props) {
  const isOpen = vendor !== null;

  return (
    <SlidePanel isOpen={isOpen} onClose={onClose} title={vendor?.company_name ?? 'Vendor'} width={520}>
      {vendor && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          <div style={STATS_ROW}>
            <Stat label="Total spent" value={fmt$(Number(vendor.total_spent ?? 0))} />
            <Stat label="Outstanding" value={fmt$(Number(vendor.outstanding_amount ?? 0))} sub={vendor.outstanding_bills > 0 ? `${vendor.outstanding_bills} bill${vendor.outstanding_bills === 1 ? '' : 's'}` : null} />
            <Stat label="Last activity" value={fmtDate(vendor.last_transaction_date)} />
          </div>

          <Section title="Contact">
            <Row label="Contact name" value={vendor.contact_name ?? '—'} />
            <Row label="Email" value={vendor.email ?? '—'} />
            <Row label="Phone" value={vendor.phone ?? '—'} />
          </Section>

          <Section title="Terms">
            <Row label="Payment terms" value={vendor.payment_terms ? vendor.payment_terms.replace('_', ' ') : '—'} />
            <Row label="Default tax type" value={vendor.default_tax_type ?? '—'} />
          </Section>

          <div style={FOOTER}>
            <button type="button" onClick={onEdit} style={PRIMARY_BTN}>
              <Edit2 size={13} style={{ marginRight: 6 }} />
              Edit vendor
            </button>
          </div>
        </div>
      )}
    </SlidePanel>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string | null }) {
  return (
    <div style={STAT}>
      <div style={STAT_LABEL}>{label}</div>
      <div style={STAT_VALUE}>{value}</div>
      {sub ? <div style={STAT_SUB}>{sub}</div> : null}
    </div>
  );
}
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={SECTION_TITLE}>{title}</div>
      <div style={SECTION_BODY}>{children}</div>
    </div>
  );
}
function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={ROW}>
      <div style={ROW_LABEL}>{label}</div>
      <div style={ROW_VALUE}>{value}</div>
    </div>
  );
}

const STATS_ROW: CSSProperties = { display: 'flex', gap: spacing.sm };
const STAT: CSSProperties = { flex: 1, background: palette.white, border: `1px solid ${admin.border}`, borderRadius: radii.sm, padding: spacing.md };
const STAT_LABEL: CSSProperties = { fontSize: 11, color: palette.g500, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' };
const STAT_VALUE: CSSProperties = { fontSize: 18, color: admin.header, fontWeight: 700, marginTop: 6, fontVariantNumeric: 'tabular-nums' };
const STAT_SUB: CSSProperties = { fontSize: 11, color: palette.g400, marginTop: 2 };
const SECTION_TITLE: CSSProperties = { fontSize: 11, color: palette.g500, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 };
const SECTION_BODY: CSSProperties = { background: palette.white, border: `1px solid ${admin.border}`, borderRadius: radii.sm };
const ROW: CSSProperties = { display: 'flex', justifyContent: 'space-between', padding: '10px 12px', borderBottom: `1px solid ${palette.g100}` };
const ROW_LABEL: CSSProperties = { fontSize: 13, color: palette.g600 };
const ROW_VALUE: CSSProperties = { fontSize: 13, color: admin.header, fontWeight: 500 };
const FOOTER: CSSProperties = { position: 'sticky', bottom: -20, marginLeft: -20, marginRight: -20, marginTop: spacing.lg, padding: `${spacing.md}px ${spacing.lg}px`, background: palette.white, borderTop: `1px solid ${admin.border}`, display: 'flex', justifyContent: 'flex-end' };
const PRIMARY_BTN: CSSProperties = { display: 'flex', alignItems: 'center', padding: '9px 22px', borderRadius: radii.sm, border: 'none', background: palette.accent, color: palette.white, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };
